import { BackButton } from '@/components/ui/BackButton';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export const PrivacyPolicy = ({ onBack }: PrivacyPolicyProps) => {
  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onBack} label="Back" srLabel="Return to previous page" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Privacy Policy</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 space-y-10">
        <div className="text-center mb-12">
          <div className="text-3xl mb-4" style={{ color: 'hsl(38, 40%, 55%)' }}>☽</div>
          <h1 className="font-display text-3xl text-ink mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: February 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">What We Collect</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Layālī collects only what is needed to provide you with a meaningful experience:
          </p>
          <ul className="text-sm text-muted-foreground leading-relaxed list-disc list-inside space-y-1.5 pl-2">
            <li>Your email address (for account creation or progress saving)</li>
            <li>Your name (optional, for personalisation)</li>
            <li>Prayer, fasting, Qurʾān, and dhikr tracking data</li>
            <li>Journal reflections and daily check-ins</li>
            <li>App preferences and onboarding choices</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">Why We Collect It</h2>
          <ul className="text-sm text-muted-foreground leading-relaxed list-disc list-inside space-y-1.5 pl-2">
            <li>To save your progress across sessions and devices</li>
            <li>To personalise your Ramadan journey</li>
            <li>To allow you to reflect on your growth over time</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">How Your Data Is Stored & Protected</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your data is securely stored using industry-standard cloud infrastructure with encryption at rest and in transit. Access to your personal data is restricted to authenticated sessions tied to your account.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">Third-Party Services</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Layālī uses the following third-party services:
          </p>
          <ul className="text-sm text-muted-foreground leading-relaxed list-disc list-inside space-y-1.5 pl-2">
            <li>Cloud database and authentication services for secure data storage</li>
            <li>Mailchimp for newsletter delivery (only if you subscribe)</li>
          </ul>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We do not sell, share, or monetise your personal data.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">Your Rights</h2>
          <ul className="text-sm text-muted-foreground leading-relaxed list-disc list-inside space-y-1.5 pl-2">
            <li>Access your data at any time through the app</li>
            <li>Request deletion of your account and all associated data</li>
            <li>Opt out of any newsletter communications</li>
            <li>Use the app without providing a name</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg text-ink">Contact</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            For any privacy-related questions, please reach out to us at{' '}
            <a href="mailto:privacy@layali.app" className="text-gold hover:underline underline-offset-2">
              privacy@layali.app
            </a>
          </p>
        </section>
      </main>
    </div>
  );
};
